import { MutationCache, QueryCache, QueryClient, VueQueryPlugin } from '@tanstack/vue-query'
import { bearerAuth, type LedgerSummary, type Principal, sessionKeys } from '@taler/api-client'
import { systemClock } from '@taler/domain'
import { createApp, ref, watch } from 'vue'
import App from '@/app/App.vue'
import { API_KEY, createApi } from '@/app/api'
import { createAuth } from '@/app/auth'
import { AUTH_KEY } from '@/app/auth/use-auth'
import { readConfig } from '@/app/config'
import { initErrorTracking } from '@/app/error-tracking'
import { createAppI18n } from '@/app/i18n'
import { readStoredActiveLedger, storeActiveLedger } from '@/app/ledger-preference'
import { createAppRouter } from '@/app/router'
import { terminalFailureOf } from '@/app/terminal-failure'
import { ACTIVE_LEDGER_KEY, createActiveLedgerRef, resolveActiveLedger } from '@/app/session'
import { readStoredLocale } from '@/app/shell/locale-preference'
import '@/styles.css'

const config = readConfig()
const activeLedgerId = createActiveLedgerRef()
// A write offline fails at once instead of waiting in the queue for a reconnect.
const queryClient = new QueryClient({
  defaultOptions: { mutations: { networkMode: 'always' } },
  queryCache: new QueryCache({ onError: (error) => routeTerminalFailure(error) }),
  mutationCache: new MutationCache({ onError: (error) => routeTerminalFailure(error) }),
})

function routeTerminalFailure(error: unknown): void {
  const failure = terminalFailureOf(error)
  if (failure === 'not-provisioned') void router.push({ name: 'not-provisioned' })
  else if (failure === 'sign-in-again') signIn()
}

const auth = createAuth({
  config,
  clock: systemClock,
  storage: window.sessionStorage,
  navigate: (url) => window.location.assign(url),
})

function signIn(): void {
  void router.push({ name: 'sign-in', query: { returnTo: router.currentRoute.value.fullPath } })
}

const api = createApi({
  baseUrl: config.apiBaseUrl,
  auth: bearerAuth(auth.provider),
  onSignInRequired: signIn,
})

const ledgersKey = sessionKeys.ledgers()
const ledgers = ref<readonly LedgerSummary[]>(
  queryClient.getQueryData<readonly LedgerSummary[]>(ledgersKey) ?? [],
)
queryClient.getQueryCache().subscribe((event) => {
  if (JSON.stringify(event.query.queryKey) !== JSON.stringify(ledgersKey)) return
  ledgers.value = queryClient.getQueryData<readonly LedgerSummary[]>(ledgersKey) ?? []
})

const principalKey = sessionKeys.principal()
const principal = ref<Principal | undefined>(queryClient.getQueryData<Principal>(principalKey))
queryClient.getQueryCache().subscribe((event) => {
  if (JSON.stringify(event.query.queryKey) !== JSON.stringify(principalKey)) return
  principal.value = queryClient.getQueryData<Principal>(principalKey)
})

// Restores the signed-in user's last active ledger once we know who they are; resolveActiveLedger
// falls back to the default itself once that ledger is no longer in their own ledger list.
watch(principal, (current) => {
  if (current !== undefined && activeLedgerId.value === undefined) {
    activeLedgerId.value = readStoredActiveLedger(current.subject)
  }
})
watch(activeLedgerId, (ledgerId) => {
  if (ledgerId !== undefined && principal.value !== undefined) {
    storeActiveLedger(principal.value.subject, ledgerId)
  }
})

const router = createAppRouter({
  activeLedger: () => resolveActiveLedger(ledgers.value, activeLedgerId.value),
})

const app = createApp(App)

initErrorTracking(app, config)

app
  .provide(API_KEY, api)
  .provide(AUTH_KEY, auth)
  .provide(ACTIVE_LEDGER_KEY, activeLedgerId)
  .use(VueQueryPlugin, { queryClient })
  .use(createAppI18n(readStoredLocale() ?? config.defaultLocale))
  .use(router)

await router.isReady()
app.mount('#app')
